import { env } from "@/env";

export type MetricName =
  // Form and test related
  | "want_app_form_submit_attempt"
  | "test_form_completed"
  | "test_form_step_selected"
  | "test_form_back_button_clicked"
  | "test_form_step_completed"

  // Landing page
  | "sample_report_clicked"

  // Child information steps
  | "child_name_step_completed"
  | "child_name_validation_success"
  | "child_name_validation_error"
  | "child_gender_selected"
  | "child_age_step_completed"
  | "age_input_incremented"
  | "age_input_decremented"
  | "age_input_selected"
  | "consent_validation_error"
  // Email related
  | "email_form_displayed"
  | "email_form_submit_attempt"
  | "email_submitted"
  | "email_submission_error"
  | "email_validation_error"
  | "email_support_clicked"
  | "email_verification"
  | "change_or_resend_clicked"
  // Questionnaire and questions
  | "question_answered"
  | "questionnaire_completed"
  | "question_answer_clicked"
  // Report actions
  | "report_shared"
  | "report_printed"
  | "read_more_clicked"
  | "want_app_modal_opened"
  | "print_ai_report"
  | "print_report"
  | "share_ai_report"
  | "share_report"
  // Tutorial and UI interactions
  | "tutorial_skipped"
  | "tutorial_next"
  | "hint_tooltip_opened"
  | "button_click"
  // Social and external links
  | "instagram_link_click"
  | "telegram_link_click"
  // Page and navigation
  | "page_view"
  // Feedback related
  | "feedback_dialog_opened"
  | "feedback_dialog_submitted"
  | "feedback_dialog_skipped"
  | "feedback_dialog_closed"
  // Payment related
  | "icon_flow_telegram_click"
  | "dialog_step_telegram_click"
  | "dialog_telegram_click"
  | "promo_code_click"
  | "enter_promo_click"
  | "payment_button_click"
  | "free_button_click"
  // Block visibility tracking
  | "hero_section_visible"
  | "questions_section_visible"
  | "steps_component_visible"
  | "result_component_visible"
  | "benefits_component_visible"
  | "faq_section_visible";

// Google Analytics 4 and Yandex Metrica implementation
declare global {
  interface Window {
    gtag: (
      command: "event" | "config" | "js",
      targetId: string,
      config?: Record<string, unknown>,
    ) => void;
    ym: (
      targetId: string,
      action: string,
      goalNameOrUrl?: string,
      params?: Record<string, unknown>,
    ) => void;
  }
}

interface PageTiming {
  page: string;
  startTime: number;
  endTime?: number;
}

class Analytics {
  private pageTimings = new Map<string, PageTiming>();
  private initialized = false;
  private debugMode = false;
  private gaId: string;
  private ymId: string;

  constructor() {
    // Check if we're on the client side
    const isClient = typeof window !== "undefined";

    if (isClient) {
      try {
        this.gaId = env.VITE_GA_ID || "";
        this.ymId = env.VITE_YM_ID || "";
      } catch {
        this.gaId = "";
        this.ymId = "";
      }
    } else {
      this.gaId = "";
      this.ymId = "";
    }
  }

  private debug(message: string, data?: Record<string, unknown>) {
    if (this.debugMode) {
      console.log(`🔍 [Analytics Debug] ${message}`, data || "");
    }
  }

  private track(eventName: string, parameters?: Record<string, unknown>) {
    if (typeof window !== "undefined" && window.self !== window.top) {
      this.debug(`Skipping event in iframe: ${eventName}`, parameters);
      return;
    }

    if (typeof window !== "undefined" && window.gtag && this.gaId) {
      window.gtag("event", eventName, {
        send_to: this.gaId,
        ...parameters,
      });
      this.debug(`GA4 Event: ${eventName}`, parameters);
    }

    if (typeof window !== "undefined" && window.ym && this.ymId) {
      window.ym(this.ymId, "reachGoal", eventName, parameters);
      this.debug(`YM Event: ${eventName}`, parameters);
    }
  }

  init() {
    if (this.initialized || typeof window === "undefined") return;

    if (window.self !== window.top) {
      this.debug("Skipping analytics initialization - running in iframe");
      return;
    }

    this.initialized = true;
    this.debug("Analytics system initialized");
    this.setupGlobalClickTracker();
    this.setupPageVisibilityTracking();
  }

  startPageTiming(page: string) {
    const timing: PageTiming = {
      page,
      startTime: Date.now(),
    };
    this.pageTimings.set(page, timing);
    this.debug(`Started timing for page: ${page}`);
  }

  endPageTiming(page: string) {
    const timing = this.pageTimings.get(page);
    if (timing) {
      timing.endTime = Date.now();
      const duration = timing.endTime - timing.startTime;

      const trackingData = {
        page_title: page,
        engagement_time_msec: duration,
        session_engaged: 1,
      };

      this.debug(`Ending timing for page: ${page}`, trackingData);

      this.track("page_view", trackingData);

      this.pageTimings.delete(page);
    }
  }

  trackFunnelEvent(event: MetricName, properties?: Record<string, unknown>) {
    const trackingData = {
      timestamp: Date.now(),
      ...(typeof window !== "undefined" && {
        page_location: window.location.href,
      }),
      ...(typeof document !== "undefined" &&
        document.referrer && { page_referrer: document.referrer }),
      ...properties,
    };

    this.debug(`Funnel Event: ${event}`, trackingData);

    this.track(event, trackingData);
  }

  private setupGlobalClickTracker() {
    this.debug("Setting up global click tracker");

    document.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      const clickableElement = this.findClickableElement(target);

      if (!clickableElement) return;

      const text = this.extractClickData(clickableElement);
      if (text) {
        this.debug("Button click detected", { button_text: text });
        this.trackFunnelEvent("button_click", { text });
      }
    });
  }

  private findClickableElement(element: HTMLElement): HTMLElement | null {
    let current = element;
    for (let i = 0; i < 5; i++) {
      if (!current) break;

      const tagName = current.tagName.toLowerCase();
      if (
        tagName === "button" ||
        tagName === "a" ||
        current.getAttribute("role") === "button" ||
        current.onclick ||
        current.style.cursor === "pointer"
      ) {
        return current;
      }
      const parent = current.parentElement;
      if (!parent) break;
      current = parent;
    }
    return null;
  }

  private extractClickData(element: HTMLElement): string | null {
    return element.textContent?.trim() || "";
  }

  private setupPageVisibilityTracking() {
    this.debug("Setting up page visibility tracking");

    let startTime = Date.now();
    let totalVisibleTime = 0;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        totalVisibleTime += Date.now() - startTime;
        this.debug("Page became hidden, pausing timer");
      } else {
        startTime = Date.now();
        this.debug("Page became visible, resuming timer");
      }
    };

    const handleBeforeUnload = () => {
      if (!document.hidden) {
        totalVisibleTime += Date.now() - startTime;
      }

      const trackingData = {
        page_title: window.location.pathname,
        engagement_time_msec: totalVisibleTime,
        session_engaged: 1,
      };

      this.debug("Page unloading, final visible time", trackingData);

      this.track("page_view", trackingData);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
  }

  trackTestFormStep(step: number, data: Record<string, unknown>) {
    const trackingData = {
      step,
      step_name: this.getStepName(step),
      ...data,
    };

    this.debug(`Test form step ${step} completed`, trackingData);

    this.trackFunnelEvent("test_form_step_completed", trackingData);
  }

  private getStepName(step: number): string {
    switch (step) {
      case 1:
        return "child_name";
      case 2:
        return "child_gender";
      case 3:
        return "child_age";
      default:
        return `step_${step}`;
    }
  }

  trackQuestionAnswer(
    questionNumber: number,
    answer: boolean,
    category?: string,
  ) {
    const trackingData = {
      question_number: questionNumber,
      answer: answer ? "yes" : "no",
      category,
    };

    this.debug(
      `Question ${questionNumber} answered: ${answer ? "yes" : "no"}`,
      trackingData,
    );

    this.trackFunnelEvent("question_answered", trackingData);
  }

  trackEmailVerification(success: boolean, questionnaireId?: string) {
    const trackingData = {
      success,
      questionnaire_id: questionnaireId,
    };

    this.debug(
      `Email verification ${success ? "successful" : "failed"}`,
      trackingData,
    );

    this.trackFunnelEvent("email_verification", trackingData);
  }

  trackCoreWebVitals(metric: {
    name: string;
    value: number;
    id: string;
    delta: number;
    rating: string;
  }) {
    const trackingData = {
      metric_name: metric.name,
      metric_value: metric.value,
      metric_id: metric.id,
      metric_delta: metric.delta,
      metric_rating: metric.rating,
    };

    this.debug(`Core Web Vital: ${metric.name}`, trackingData);
    this.track("web_vital", trackingData);
  }

  setDebugMode(enabled: boolean) {
    this.debugMode = enabled;
    this.debug(`Debug mode ${enabled ? "enabled" : "disabled"}`);
  }
}

export const analytics = new Analytics();
